import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import i18n from '@/core/i18n';
import { EntityAvatar } from '@/modules/common';
import { PaginatedResponse } from '@/modules/common/types/pagination-types';
import { logger } from '@/utils/logger/logger';

const TAG = 'Dropdown:';

export const RadioDropdown = ({
  options,
  children,
  value,
  onChange,
  label,
}: {
  options: { label: string; value: string; disabled?: boolean }[];
  children: React.ReactNode;
  value: string;
  onChange: (value: string) => void;
  label: string;
}) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline">{children}</Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56">
        <DropdownMenuLabel>{label}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuRadioGroup value={value} onValueChange={onChange}>
          {options.map(option => (
            <DropdownMenuRadioItem
              key={option.value}
              disabled={option.disabled}
              value={option.value}
            >
              {option.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export function DropdownCheckboxes({
  options,
  children,
  values,
  onChange,
  label,
}: {
  options: { label: string; value: string }[];
  children: React.ReactNode;
  values: string[];
  onChange: (value: string[]) => void;
  label: string;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline">{children}</Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56">
        <DropdownMenuLabel>{label}</DropdownMenuLabel>
        <DropdownMenuSeparator />

        {options.map(option => (
          <DropdownMenuCheckboxItem
            key={option.value}
            checked={values.includes(option.value)}
            onSelect={e => e.preventDefault()}
            onCheckedChange={checked => {
              onChange(
                checked ? [...values, option.value] : values.filter(v => v !== option.value)
              );
            }}
          >
            {option.label}
          </DropdownMenuCheckboxItem>
        ))}
        <DropdownMenuSeparator />

        <DropdownMenuItem className="flex justify-end">
          <Button variant="outline" onClick={() => onChange([])} size="sm">
            {i18n.t('common:clear')}
          </Button>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export interface DropdownSearchItem {
  id: string;
  name: string;
  avatarLink?: string;
  secondaryText?: string;
  colorHex?: string | null;
  isDeleted?: boolean;
  status?: string;
  /** Effective date of the nearest future change that archives this entity (projects/short). */
  nextArchiveDate?: string | null;
  /** Effective date of the nearest future change that unarchives this entity (projects/short). */
  nextUnarchiveDate?: string | null;
  /**
   * Marks this item as belonging to the "primary" (highlighted-first) section of a
   * grouped list — e.g. projects the current member is already booked on in the
   * time-tracker project picker. See `groupKey` on `SingleSelectOption`.
   */
  isInPrimaryGroup?: boolean;
}

interface DropdownWithSearchAndPaginationProps {
  onSelectItem: (item: DropdownSearchItem) => void;
  loadItems: (
    searchString?: string,
    cursor?: number | null
  ) => Promise<PaginatedResponse<DropdownSearchItem>>;
  searchPlaceholder: string;
  emptyMessage: string;
  loadingMessage: string;
  children: React.ReactNode;
  disabled?: boolean;
  doShowAvatar?: boolean;
  doShowSecondaryText?: boolean;
  /** Number of items to exclude from dropdown (used to invalidate cache when selection changes) */
  excludedItemsCount?: number;
  /** Button variant for the trigger button */
  buttonVariant?: 'ghost' | 'default' | 'outline' | 'secondary' | 'destructive' | 'link';
}

/**
 * DropdownWithSearch component for searching and selecting items with pagination support.
 * Supports infinite scroll, async search, avatars, and secondary text.
 */
export function DropdownWithSearchAndPagination({
  onSelectItem,
  loadItems,
  searchPlaceholder,
  emptyMessage,
  loadingMessage,
  children,
  disabled = false,
  doShowAvatar = true,
  doShowSecondaryText = false,
  excludedItemsCount = 0,
  buttonVariant = 'ghost',
}: DropdownWithSearchAndPaginationProps) {
  const { t } = useTranslation('common');
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [items, setItems] = useState<DropdownSearchItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [currentCursor, setCurrentCursor] = useState<number | null>(null);
  const [hasPerformedSearch, setHasPerformedSearch] = useState(false);

  // Invalidate cache when excluded items count changes (e.g., when items are selected)
  useEffect(() => {
    if (excludedItemsCount > 0) {
      setItems([]);
      setCurrentCursor(null);
      setHasMore(true);
      setHasPerformedSearch(false);
    }
  }, [excludedItemsCount]);

  const loadInitialItems = async () => {
    setIsLoading(true);
    setCurrentCursor(null);

    try {
      const result = await loadItems(undefined, null);
      setItems(result.items);
      setHasMore(result.pagination.cursor !== null);
      setCurrentCursor(result.pagination.cursor);
    } catch (error) {
      logger.error(TAG, 'Error loading initial items:', error);
      setItems([]);
      setHasMore(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Load initial items only on first open
  useEffect(() => {
    if (isOpen && items.length === 0 && !isLoading) {
      loadInitialItems();
    }
  }, [isOpen]);

  // Handle search term changes
  useEffect(() => {
    if (!isOpen) return;

    const searchAsync = async () => {
      const trimmedSearch = searchTerm.trim();

      // If search is cleared after searching, reload initial items
      if (trimmedSearch.length === 0) {
        if (hasPerformedSearch) {
          setHasPerformedSearch(false);
          loadInitialItems();
        }
        return;
      }

      // Trigger search for 2+ characters
      setIsLoading(true);
      setCurrentCursor(null);
      setHasPerformedSearch(true);

      try {
        const result = await loadItems(searchTerm, null);
        setItems(result.items);
        setHasMore(result.pagination.cursor !== null);
        setCurrentCursor(result.pagination.cursor);
      } catch (error) {
        logger.error(TAG, 'Error searching items:', error);
        setItems([]);
        setHasMore(false);
      } finally {
        setIsLoading(false);
      }
    };

    const debounceTimer = setTimeout(searchAsync, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchTerm, isOpen, loadItems]);

  const loadMoreItems = async () => {
    if (isLoadingMore || !hasMore) {
      return;
    }

    setIsLoadingMore(true);
    try {
      const result = await loadItems(searchTerm.trim() || undefined, currentCursor);

      setItems(prev => [...prev, ...result.items]);
      setHasMore(result.pagination.cursor !== null);
      setCurrentCursor(result.pagination.cursor);
    } catch (error) {
      logger.error(TAG, 'Error loading more items in DropdownWithSearch:', error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setSearchTerm('');
    }
  };

  const handleSelectItem = (item: DropdownSearchItem) => {
    onSelectItem(item);
    handleOpenChange(false);
  };

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button variant={buttonVariant} className="justify-start" disabled={disabled}>
          {children}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={searchPlaceholder}
            value={searchTerm}
            onValueChange={setSearchTerm}
            className="border-0 focus:ring-0"
          />
          <CommandList
            className="max-h-60"
            onScroll={e => {
              const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
              if (scrollHeight - scrollTop <= clientHeight + 5) {
                loadMoreItems();
              }
            }}
          >
            {items.length === 0 && !isLoading ? (
              <CommandEmpty>{emptyMessage}</CommandEmpty>
            ) : (
              <CommandGroup>
                {isLoading && items.length === 0 ? (
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    {loadingMessage}
                  </div>
                ) : (
                  <>
                    {items.map(item => (
                      <CommandItem
                        key={item.id}
                        onSelect={() => handleSelectItem(item)}
                        className="cursor-pointer py-2"
                        disabled={isLoading}
                      >
                        <div className="flex w-full items-center gap-3">
                          {doShowAvatar && (
                            <EntityAvatar
                              image={item.avatarLink}
                              name={item.name}
                              size="sm"
                              className="flex-shrink-0"
                            />
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="truncate">{item.name}</div>
                            {doShowSecondaryText && item.secondaryText && (
                              <div className="truncate text-xs text-muted-foreground">
                                {item.secondaryText}
                              </div>
                            )}
                          </div>
                        </div>
                      </CommandItem>
                    ))}
                    {isLoading && items.length > 0 && (
                      <div className="py-2 text-center text-xs text-muted-foreground">
                        {loadingMessage}
                      </div>
                    )}
                    {isLoadingMore && (
                      <div className="py-2 text-center text-xs text-muted-foreground">
                        {t('loadingMore')}
                      </div>
                    )}
                  </>
                )}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
